import React, { useState, useEffect } from 'react';
import { X, LayoutTemplate, Type, FileText, Image, Palette, Move } from 'lucide-react';

interface HeaderSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    settings: {
        title: string;
        description: string;
        logoUrl?: string;
        showTitle?: boolean;
        showLogo?: boolean;
        // New Style Props
        titleStyle?: {
            color?: string;
            fontSize?: number;
            bold?: boolean;
            italic?: boolean;
            underline?: boolean;
        };
        canvas?: {
            backgroundColor?: string;
            margin?: number;
        };
    };
    onSave: (settings: any) => void;
}

export function HeaderSettingsModal({ isOpen, onClose, settings, onSave }: HeaderSettingsModalProps) {
    // Normalize defaults
    const [formData, setFormData] = useState({
        ...settings,
        titleStyle: {
            color: '#1e293b',
            fontSize: 20,
            bold: true,
            italic: false,
            underline: false,
            ...(settings.titleStyle || {})
        },
        canvas: {
            backgroundColor: '#f8fafc', // slate-50
            margin: 32,
            ...(settings.canvas || {})
        }
    });

    const prevOpenRef = React.useRef(false);

    useEffect(() => {
        // Only re-sync from props when the modal OPENS (false → true transition)
        // This prevents the color picker from resetting to the saved value
        // while the user is still dragging/picking a color.
        if (isOpen && !prevOpenRef.current) {
            setFormData({
                ...settings,
                titleStyle: {
                    color: '#1e293b',
                    fontSize: 20,
                    bold: true,
                    italic: false,
                    underline: false,
                    ...(settings.titleStyle || {})
                },
                canvas: {
                    backgroundColor: '#f8fafc',
                    margin: 32,
                    ...(settings.canvas || {})
                }
            });
        }
        prevOpenRef.current = isOpen;
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
        onClose();
    };

    const updateTitleStyle = (key: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            titleStyle: { ...prev.titleStyle, [key]: value }
        }));
    };

    const updateCanvas = (key: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            canvas: { ...prev.canvas, [key]: value }
        }));
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200 flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700 shrink-0">
                    <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        <LayoutTemplate className="w-5 h-5 text-blue-600" />
                        Dashboard Settings
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
                    <form id="settings-form" onSubmit={handleSubmit} className="space-y-6">



                        <div className="space-y-4 pt-4">
                            {/* Description */}
                            <div className="space-y-3">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Description (for Dashboard Card)
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent resize-none text-sm"
                                    placeholder="Enter a brief description..."
                                />
                            </div>
                        </div>

                        {/* 2. CANVAS CONFIGURATION */}
                        <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                            <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider border-b pb-1">Canvas Styling</h3>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Background Color */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                        <Palette className="w-4 h-4" />
                                        Background
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={formData.canvas.backgroundColor}
                                            onChange={e => updateCanvas('backgroundColor', e.target.value)}
                                            className="h-9 w-12 p-0 border border-slate-300 rounded cursor-pointer"
                                        />
                                        <span className="text-xs text-slate-500">{formData.canvas.backgroundColor}</span>
                                    </div>
                                </div>

                                {/* Margin */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                        <Move className="w-4 h-4" />
                                        Canvas Padding
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            value={formData.canvas.margin}
                                            onChange={e => updateCanvas('margin', Number(e.target.value))}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                            min={0} max={100}
                                        />
                                        <span className="text-xs text-slate-500">px</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </form>
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-700 shrink-0 bg-slate-50 dark:bg-slate-900/50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="settings-form"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
                    >
                        Apply Settings
                    </button>
                </div>
            </div>
        </div>
    );
}
